import React, { useState, useEffect, useCallback } from "react";
import { FaUsers } from "react-icons/fa";
import { LuPlus, LuUserPlus } from "react-icons/lu";
import axios from "axios";
import ContactManagement from "./ContactManagement";
import QueryManagement from "../QueryManagement/QueryManagement";
import WebinarManagement from "../WebinarManagement/WebinarManagement";
import LeadManagement from "../LeadManagement/LeadManagement";
import PageLayout from "../../components/ui/PageLayout";
import PageHeader from "../../components/ui/PageHeader";
import { PillTabs } from "../../components/ui/TabBar";
import AddManualContactModal from "./AddManualContactModal";

const ContactsHub = () => {
  const [activeTab, setActiveTab] = useState("forms");
  const [showAddModal, setShowAddModal] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);

  const [counts, setCounts] = useState({
    forms: 0,
    queries: 0,
    webinar: 0,
    leads: 0,
  });

  const fetchAllCounts = useCallback(async () => {
    const token = localStorage.getItem("adminToken");
    const baseUrl = process.env.REACT_APP_API_BASE_URL || "http://localhost:5000";

    try {
      const [formsRes, queriesRes, webinarRes, leadsRes] = await Promise.all([
        axios.get(`${baseUrl}/api/contact/admin/all`, {
          headers: { Authorization: `Bearer ${token}` }
        }).catch(() => ({ data: { success: false } })),
        axios.get(`${baseUrl}/api/contact/admin/queries`, {
          headers: { Authorization: `Bearer ${token}` }
        }).catch(() => ({ data: { success: false } })),
        axios.get(`${baseUrl}/api/webinar/admin/all`, {
          headers: { Authorization: `Bearer ${token}` }
        }).catch(() => ({ data: { success: false } })),
        axios.get(`${baseUrl}/api/leads`, {
          headers: { Authorization: `Bearer ${token}` }
        }).catch(() => ({ data: { success: false } }))
      ]);

      setCounts({
        forms: formsRes.data.success ? formsRes.data.data.length : 0,
        queries: queriesRes.data.success ? queriesRes.data.data.length : 0,
        webinar: webinarRes.data.success ? webinarRes.data.data.length : 0,
        leads: leadsRes.data.success ? leadsRes.data.data.length : 0
      });
    } catch (error) {
      console.error("Failed to fetch initial contact counts:", error);
    }
  }, []);

  useEffect(() => {
    fetchAllCounts();
  }, [fetchAllCounts, refreshKey]);

  const handleCountChange = useCallback((tabId, count) => {
    setCounts((prev) => ({ ...prev, [tabId]: count }));
  }, []);

  const handleManualAddSuccess = (tabCreated) => {
    if (tabCreated) {
      setActiveTab(tabCreated);
    }
    setRefreshKey((prev) => prev + 1);
  };

  const tabs = [
    { id: "forms", label: "Contact Forms", badge: counts.forms },
    { id: "queries", label: "Queries", badge: counts.queries },
    { id: "webinar", label: "Webinar Form", badge: counts.webinar },
    { id: "leads", label: "Landing Leads", badge: counts.leads },
  ];

  return (
    <PageLayout>
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
        <PageHeader
          title="Contacts Hub"
          description="Manage contact forms, queries, webinar registrations, and landing leads"
          icon={FaUsers}
          iconGradient="from-primary-500 to-primary-600"
        />

        {/* Add Entry Action Button */}
        <button
          onClick={() => setShowAddModal(true)}
          className="inline-flex items-center justify-center gap-2 px-5 py-3 rounded-2xl bg-indigo-600 hover:bg-indigo-700 text-white font-bold text-xs sm:text-sm transition-all shadow-md hover:shadow-lg cursor-pointer transform hover:-translate-y-0.5"
        >
          <LuUserPlus className="w-4 h-4" />
          <span>+ Add Entry (Call/Manual)</span>
        </button>
      </div>

      <PillTabs tabs={tabs} activeTab={activeTab} onChange={setActiveTab} />

      <div className="bg-white dark:bg-dark-800 rounded-2xl shadow-card border border-gray-100 dark:border-dark-700 overflow-hidden">
        {activeTab === "forms" && (
          <ContactManagement
            key={`forms-${refreshKey}`}
            onCountChange={(count) => handleCountChange("forms", count)}
          />
        )}
        {activeTab === "queries" && (
          <QueryManagement
            key={`queries-${refreshKey}`}
            onCountChange={(count) => handleCountChange("queries", count)}
          />
        )}
        {activeTab === "webinar" && (
          <WebinarManagement
            key={`webinar-${refreshKey}`}
            onCountChange={(count) => handleCountChange("webinar", count)}
          />
        )}
        {activeTab === "leads" && (
          <LeadManagement
            key={`leads-${refreshKey}`}
            onCountChange={(count) => handleCountChange("leads", count)}
          />
        )}
      </div>

      {/* Manual Entry Creation Modal */}
      <AddManualContactModal
        isOpen={showAddModal}
        onClose={() => setShowAddModal(false)}
        initialTab={activeTab}
        onSuccess={handleManualAddSuccess}
      />
    </PageLayout>
  );
};

export default ContactsHub;
